import { InstructorLinkTags } from "./types";
import { INSTRUCTOR_LINK_TAG_KEYS, isoToDate, isoToDisplay } from "./utils";

export type TagDateErrorCode =
  | "out-of-period"
  | "payment-end-before-start"
  | "drop-before-assignment"
  | "assignment-after-payment-end";

export interface TagDateError {
  /** Tag key the error is attached to (used to highlight the offending field). */
  key: string;
  code: TagDateErrorCode;
  /** Pre-formatted display values for message interpolation in the UI layer. */
  params: Record<string, string>;
}

export interface TagPeriod {
  start?: string | null;
  end?: string | null;
}

/** Date tags that must fall within the academic period (AC7). */
const RANGE_CHECK_KEYS = [
  INSTRUCTOR_LINK_TAG_KEYS.paymentStartDate,
  INSTRUCTOR_LINK_TAG_KEYS.paymentEndDate,
  INSTRUCTOR_LINK_TAG_KEYS.dropDate,
];

/**
 * Validates the date tags of a single instructor link against the academic
 * period range (AC7) and the chronological order rules (AC8/AC9). Returns one
 * descriptor per broken rule; the UI layer translates `code` + `params` into a
 * localized message and highlights the field identified by `key`.
 */
export const validateTagDates = (
  tags: InstructorLinkTags = {},
  period: TagPeriod = {},
): TagDateError[] => {
  const errors: TagDateError[] = [];

  const assignment = isoToDate(tags[INSTRUCTOR_LINK_TAG_KEYS.assignmentDate]);
  const paymentStart = isoToDate(tags[INSTRUCTOR_LINK_TAG_KEYS.paymentStartDate]);
  const paymentEnd = isoToDate(tags[INSTRUCTOR_LINK_TAG_KEYS.paymentEndDate]);
  const drop = isoToDate(tags[INSTRUCTOR_LINK_TAG_KEYS.dropDate]);

  const periodStart = isoToDate(period.start);
  const periodEnd = isoToDate(period.end);

  // AC7 — date within the academic period
  if (periodStart && periodEnd) {
    RANGE_CHECK_KEYS.forEach(key => {
      const date = isoToDate(tags[key]);
      const isOutOfPeriod =
        date && (date.getTime() < periodStart.getTime() || date.getTime() > periodEnd.getTime());

      if (isOutOfPeriod) {
        errors.push({
          key,
          code: "out-of-period",
          params: {
            date: isoToDisplay(tags[key]),
            start: isoToDisplay(period.start),
            end: isoToDisplay(period.end),
          },
        });
      }
    });
  }

  // AC8 — payment end after payment start
  if (paymentStart && paymentEnd && paymentEnd.getTime() <= paymentStart.getTime()) {
    errors.push({
      key: INSTRUCTOR_LINK_TAG_KEYS.paymentEndDate,
      code: "payment-end-before-start",
      params: { date: isoToDisplay(tags[INSTRUCTOR_LINK_TAG_KEYS.paymentEndDate]) },
    });
  }

  // AC9 — drop after assignment
  if (assignment && drop && drop.getTime() <= assignment.getTime()) {
    errors.push({
      key: INSTRUCTOR_LINK_TAG_KEYS.dropDate,
      code: "drop-before-assignment",
      params: { date: isoToDisplay(tags[INSTRUCTOR_LINK_TAG_KEYS.dropDate]) },
    });
  }

  // Assignment must not be after payment end
  if (assignment && paymentEnd && assignment.getTime() > paymentEnd.getTime()) {
    errors.push({
      key: INSTRUCTOR_LINK_TAG_KEYS.assignmentDate,
      code: "assignment-after-payment-end",
      params: { date: isoToDisplay(tags[INSTRUCTOR_LINK_TAG_KEYS.assignmentDate]) },
    });
  }

  return errors;
};
